import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { useState, useRef, memo, useEffect } from "react";
import OverviewHeader from "src/modules/3rd-party-management/pages/admin/carrier-guard/carriers/details/components/overview-header";
import { Button, Col, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { connect } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import CompanyDetails from "./components/company-details";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from "src/services/utils/DateUtils";
import { KYBAvailableServices } from "src/modules/3rd-party-management/constants";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';

const CachedOrganizations = memo(({
    t,
    supplierData
}) => {
    const history = useHistory();
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    });

    const tableColumns = [
        {
            dataField: "source",
            text: t("Source"),
            sort: true,
            key: 1,
            style: {
                width: '120px'
            },
            formatter: (cell) => {
                return getSourceTitle(cell)
            }
        },
        {
            dataField: "id_in_source",
            text: t("Source ID"),
            sort: true,
            style: {
                width: '120px'
            },
            key: 2
        },
        {
            dataField: "id",
            text: t("SIP ID"),
            sort: true,
            style: {
                width: '80px'
            },
            key: 3
        },
        {
            dataField: "primary_name",
            text: t("Company Name"),
            sort: false,
            style: {
                width: '120px'
            },
            key: 4
        },
        {
            dataField: "short_name",
            text: t("Short Name"),
            sort: false,
            style: {
                width: '100px'
            },
            key: 5
        },
        {
            dataField: "establishment_address_country",
            text: t("Country"),
            sort: false,
            style: {
                width: '100px'
            },
            key: 6
        },
        {
            dataField: "business_number",
            text: t("Registration Number"),
            sort: false,
            style: {
                width: '140px'
            },
            key: 7
        },
        {
            dataField: "duns",
            text: t("DUNS"),
            sort: true,
            style: {
                width: '100px'
            },
            key: 8
        },
        {
            dataField: "created_at",
            text: t("Created On"),
            sort: false,
            key: 9,
            style: {
                width: '100px'
            },
            formatter: (cell) => {
                return (
                    dateUtils.convertTimeStampToDate(
                        cell,
                        INTERNATIONAL_DATE_FORMAT
                    )
                )
            }
        },
        {
            dataField: "created_by",
            text: t("Created By"),
            sort: false,
            style: {
                width: '100px'
            },
            key: 10,
            formatter: (cell) => {
                return (
                    cell?.full_name || cell?.fullName || '--'
                )
            }
        },
        {
            dataField: "address",
            text: t("Address"),
            sort: false,
            key: 11,
            style: {
                width: '140px'
            },
            formatter: (cell, row) => {
                return (
                    `${row.establishment_address}, ${row.establishment_address_city || ''}, ${row.establishment_address_country || ''}`
                )
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: false,
            key: 12,
            style: {
                width: '100px'
            },
            formatter: (cell, row) => {
                return (
                    <div className="d-flex">
                        <Button
                            color="primary"
                            aria-label={`Fetch details for ${row.primary_name}`}
                            onClick={() => {
                                history.push({
                                    search: `?tab=kyb&orgId=${row.id_in_source}&provider=${row.source}`
                                })
                            }}
                            outline
                            className="border-0"
                            id={`view-btn-${row.id}`}
                        >
                            <i className="ri-eye-line font-size-20" />
                        </Button>

                        <UncontrolledTooltip
                            target={`view-btn-${row.id}`}
                        >
                            {t('View details')}
                        </UncontrolledTooltip>

                        {/* <Button
                                color="primary"
                                outline
                                className="border-0"
                                id={`download-btn-${org.id}`}
                            >
                                <i className="ri-download-line font-size-20" />
                            </Button>

                            <UncontrolledTooltip
                                target={`download-btn-${org.id}`}
                            >
                                {t('Download PDF')}
                            </UncontrolledTooltip> */}
                    </div>
                )
            }
        }
    ];

    const {
        data: organizations,
        isFetching: isFetchingCachedOrganizations,
        isFetched: organizationsFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-kyb-cached-organizations',
            supplierData.id
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();

            return await service.fetchCachedOrganizations(supplierData.id, {});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the organizations list. Please try again later.'), {
                type: 'error'
            });
        }
    });

    const NoDataIndication = () => (
            (organizationsFetched && !organizations?.meta?.count) ? <div className="alert m-0" role="alert">
                <p style={{
                    textAlign: "center",
                    marginBottom: 0
                }}>
                    {t(EMPTY_LIST)}
                </p>
            </div> : <></>
        );

    const getSourceTitle = (source) => {
        return (KYBAvailableServices.find((service) => service.id === source))?.title || 'Dun & Bradstreet'; //temp
    }

    const dateUtils = new DateUtils();

    return (
        <PaginationProvider
            pagination={
                paginationFactory({
                    custom: true,
                    page: filters.pageIndex,
                    sizePerPage: filters.pageSize,
                    totalSize: organizations?.meta?.count || 0,
                    withFirstAndLast: true,
                    alwaysShowAllBtns: true,
                    prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                    nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                    prePageTitle: t('Pre page'),
                    firstPageTitle: t('First page'),
                    lastPageTitle: t('Last page'),
                    showTotal: false,
                    paginationTotalRenderer: (from, to, size) => {
                        return (
                            <span className="react-bootstrap-table-pagination-total">
                                {`${t('Total Items')} ${size}`}
                            </span>
                        )
                    },
                    hideSizePerPage: false,
                    sizePerPageList: [
                        {
                            text: '25',
                            value: 25
                        },
                        {
                            text: '50',
                            value: 50
                        }
                    ]
                })
            }>
            {
                ({
                    paginationProps,
                    paginationTableProps
                }) => (
                    <>
                        <Row>
                            <Col sm="12" className="mt-4">
                                <BootstrapTable
                                    loading={isFetchingCachedOrganizations}
                                    overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                    defaultSorted={[]}
                                    keyField="id"
                                    responsive={true}
                                    bordered={false}
                                    data={organizations?.data || []}
                                    striped={false}
                                    columns={tableColumns}
                                    wrapperClasses="table-responsive"
                                    classes={"table tpdd-table compliance-monitoring-table"}
                                    headerWrapperClasses={"thead-light"}
                                    style={{
                                        overflowX: "auto",
                                    }}
                                    noDataIndication={() => <NoDataIndication />}
                                    {...paginationTableProps}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col sm="12">
                                <div className="tpdd-pagination-style-1">
                                    <PaginationTotalStandalone
                                        {...paginationProps}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                )
            }

        </PaginationProvider>
    )
});

const KYBTabContent = ({
    t,
    supplierData
}) => {
    const [ selectedOrgId, setSelectedOrgId ] = useState(null);
    const [ selectedProvider, setSelectedProvider ] = useState(null);

    const history = useHistory();
    const location = useLocation();

    const overviewHeaderRef = useRef();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        if (searchParams.has('orgId')) {
            setSelectedOrgId(searchParams.get('orgId'))
            setSelectedProvider(searchParams.get('provider'))
        }
        else {
            setSelectedOrgId(null)
        }
    }, [ location.search ]);

    return (
        <div>
            <OverviewHeader 
                supplierId={ supplierData.id } 
                actionsRef={ overviewHeaderRef } 
            />

            <Row className="mt-4 mb-4">
                {selectedOrgId ? (
                    <Col sm="12">
                        <CompanyDetails 
                            id={selectedOrgId}
                            provider={selectedProvider}
                            supplierData={supplierData}
                        />
                    </Col>
                ) : (
                    <>
                        <Col sm="12" className="d-flex align-items-center justify-content-end gap-2 mb-4">
                            <Button
                                color="primary"
                                onClick={() => {
                                    history.push({
                                        search: `?tab=kyb&page=add-company`
                                    })
                                }}
                            >
                                <i className="ri-add-line me-1" />
                                {t('Add Company Report')}
                            </Button>
                        </Col>

                        <Col sm="12">
                            <CachedOrganizations
                                supplierData={supplierData}
                                t={t}
                            />
                        </Col>
                    </>
                )}

            </Row>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { user } = state.Login;

    return {
        user
    };
};

export default withNamespaces()(
    connect(mapStatetoProps, {})(
        withRouter(KYBTabContent)
    )
); 